import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AppModal from '../components/AppModal';
import CopyLinkButton from '../components/CopyLinkButton';
import { API_BASE_URL } from '../api';
import { canManageTestRuns } from '../permissions';
import { isOptionalHttpUrl } from '../validation';
import './TestExecutionPage.css';

const runShareUrl = (runId) => `${window.location.origin}/test-runs/${runId}`;

function TestExecutionPage({ user }) {
  const navigate = useNavigate();
  const canManage = canManageTestRuns(user);
  const [projects, setProjects] = useState([]);
  const [suites, setSuites] = useState([]);
  const [testCases, setTestCases] = useState([]);
  const [testRuns, setTestRuns] = useState([]);

  const [selectedProject, setSelectedProject] = useState('');
  const [selectedSuite, setSelectedSuite] = useState('');
  const [selectedTestCases, setSelectedTestCases] = useState([]);
  const [currentTestIndex, setCurrentTestIndex] = useState(0);
  const [activeTestCases, setActiveTestCases] = useState([]);
  const [activeSelectedTestCases, setActiveSelectedTestCases] = useState([]);
  const [caseComment, setCaseComment] = useState('');

  const [runName, setRunName] = useState('');
  const [ticketUrl, setTicketUrl] = useState('');
  const [prUrl, setPrUrl] = useState('');
  const [runFormError, setRunFormError] = useState('');
  const [showCreateRun, setShowCreateRun] = useState(false);
  const [executingRun, setExecutingRun] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [creatingRun, setCreatingRun] = useState(false);
  const [resumingRunId, setResumingRunId] = useState(null);
  const [submittingResult, setSubmittingResult] = useState(false);
  const creatingRunRef = useRef(false);
  const resumingRunRef = useRef(null);
  const submittingResultRef = useRef(false);
  const [modalState, setModalState] = useState({
    open: false,
    title: '',
    message: ''
  });

  const resetExecutionState = useCallback(() => {
    setExecutingRun(null);
    setCurrentTestIndex(0);
    setTestResults({});
    setActiveTestCases([]);
    setActiveSelectedTestCases([]);
    setCaseComment('');
  }, []);

  const token = localStorage.getItem('token');

  const showModal = (title, message) => {
    setModalState({ open: true, title, message });
  };

  const closeModal = () => {
    setModalState({ open: false, title: '', message: '' });
  };

  const fetchProjects = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setProjects(data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  }, [token]);

  const fetchAllTestRuns = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/testruns`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();

        if (Array.isArray(data)) {
          setTestRuns(data);
          return;
        }
      }

      throw new Error('All-runs endpoint unavailable');
    } catch (error) {
      console.warn('Falling back to aggregated test run loading:', error);

      try {
        const projectsResponse = await fetch(`${API_BASE_URL}/projects`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!projectsResponse.ok) {
          throw new Error('Failed to fetch projects for fallback');
        }

        const allProjects = await projectsResponse.json();
        const projectRuns = await Promise.all(
          allProjects.map(async (project) => {
            const runsResponse = await fetch(`${API_BASE_URL}/testruns/project/${project.id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!runsResponse.ok) {
              return [];
            }

            const runs = await runsResponse.json();
            return runs.map((run) => ({
              ...run,
              project_name: project.name
            }));
          })
        );

        const flattenedRuns = projectRuns
          .flat()
          .sort((left, right) => new Date(right.created_at) - new Date(left.created_at));

        setTestRuns(flattenedRuns);
      } catch (fallbackError) {
        console.error('Error fetching all test runs:', fallbackError);
        setTestRuns([]);
      }
    }
  }, [token]);

  useEffect(() => {
    fetchProjects();
    fetchAllTestRuns();
  }, [fetchProjects, fetchAllTestRuns]);

  const fetchSuites = async (projectId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/suites/project/${projectId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSuites(data);
    } catch (error) {
      console.error('Error fetching suites:', error);
    }
  };

  const fetchTestCases = async (suiteId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/testcases/suite/${suiteId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setTestCases(data);
      setSelectedTestCases(data.map(tc => tc.id));
    } catch (error) {
      console.error('Error fetching test cases:', error);
    }
  };

  const handleProjectChange = (projectId) => {
    setSelectedProject(projectId);
    setSelectedSuite('');
    setTestCases([]);
    setSelectedTestCases([]);
    if (projectId) {
      fetchSuites(projectId);
    }
  };

  const handleSuiteChange = (suiteId) => {
    setSelectedSuite(suiteId);
    if (suiteId) {
      fetchTestCases(suiteId);
    }
  };

  const handleCreateRun = async (e) => {
    e.preventDefault();
    setRunFormError('');
    if (!runName.trim()) {
      showModal('Run Name Required', 'Please enter a test run name.');
      return;
    }
    if (!isOptionalHttpUrl(ticketUrl) || !isOptionalHttpUrl(prUrl)) {
      setRunFormError('Ticket and PR links must be valid http(s) URLs, or left blank.');
      return;
    }
    if (creatingRunRef.current) return;
    creatingRunRef.current = true;
    setCreatingRun(true);

    try {
      const response = await fetch(`${API_BASE_URL}/testruns`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          projectId: parseInt(selectedProject),
          name: runName,
          testCaseIds: selectedTestCases,
          ticketUrl: ticketUrl.trim() || null,
          prUrl: prUrl.trim() || null
        })
      });

      const data = await response.json();
      if (response.ok) {
        setActiveTestCases(testCases);
        setActiveSelectedTestCases(selectedTestCases);
        setExecutingRun(data);
        setCurrentTestIndex(0);
        setTestResults({});
        setCaseComment('');
        setRunName('');
        setTicketUrl('');
        setPrUrl('');
        setShowCreateRun(false);
        setSelectedProject('');
        setSelectedSuite('');
        setSuites([]);
        setTestCases([]);
        setSelectedTestCases([]);
        fetchAllTestRuns();
      } else {
        setRunFormError(data.error || 'Failed to create test run.');
      }
    } catch (error) {
      console.error('Error creating test run:', error);
      showModal('Create Run Failed', 'Error creating test run: ' + error.message);
    } finally {
      creatingRunRef.current = false;
      setCreatingRun(false);
    }
  };

  const handleResumeRun = async (runId) => {
    if (resumingRunRef.current) return;
    resumingRunRef.current = runId;
    setResumingRunId(runId);
    try {
      const response = await fetch(`${API_BASE_URL}/testruns/${runId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to load test run');
      }

      const run = await response.json();
      const runCases = Array.isArray(run.test_cases) ? run.test_cases.filter((testCase) => testCase?.id) : [];
      const runCaseIds = runCases.map((testCase) => testCase.id);

      if (runCaseIds.length === 0) {
        showModal('Resume Failed', 'This run has no saved test case selection to continue from.');
        return;
      }

      const latestResultsByTestCase = (run.results || []).reduce((accumulator, result) => {
        if (!accumulator[result.test_case_id]) {
          accumulator[result.test_case_id] = result;
        }
        return accumulator;
      }, {});

      const resumedResults = Object.fromEntries(
        Object.entries(latestResultsByTestCase).map(([testCaseId, result]) => [
          testCaseId,
          {
            status: result.status,
            result
          }
        ])
      );

      const nextPendingIndex = runCaseIds.findIndex((testCaseId) => !latestResultsByTestCase[testCaseId]);

      if (nextPendingIndex === -1) {
        showModal('Run Already Finished', 'All test cases in this run already have results. Complete the run to close it out.');
        return;
      }

      setExecutingRun(run);
      setActiveTestCases(runCases);
      setActiveSelectedTestCases(runCaseIds);
      setTestResults(resumedResults);
      setCurrentTestIndex(nextPendingIndex);
      setCaseComment('');
      setShowCreateRun(false);
    } catch (error) {
      console.error('Error resuming test run:', error);
      showModal('Resume Failed', 'Error resuming test run: ' + error.message);
    } finally {
      resumingRunRef.current = null;
      setResumingRunId(null);
    }
  };

  const getCurrentTest = () => {
    if (!executingRun || currentTestIndex >= activeSelectedTestCases.length) return null;
    return activeTestCases.find(tc => tc.id === activeSelectedTestCases[currentTestIndex]);
  };

  const handleTestResult = async (status) => {
    if (submittingResultRef.current) return;
    submittingResultRef.current = true;
    setSubmittingResult(true);

    const testCaseId = activeSelectedTestCases[currentTestIndex];

    const startTime = Date.now();

    try {
      const response = await fetch(`${API_BASE_URL}/testruns/${executingRun.id}/results`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          testCaseId: testCaseId,
          status: status,
          durationMs: Date.now() - startTime,
          detailedLogs: `Execution completed at ${new Date().toLocaleString()}`,
          comment: caseComment.trim() || null
        })
      });

      if (response.ok) {
        const result = await response.json();
        setTestResults({
          ...testResults,
          [testCaseId]: { status, result }
        });
        setCaseComment('');

        if (currentTestIndex + 1 < activeSelectedTestCases.length) {
          setCurrentTestIndex(currentTestIndex + 1);
        } else {
          completeTestRun();
        }
      }
    } catch (error) {
      console.error('Error recording test result:', error);
      showModal('Record Result Failed', 'Error recording result: ' + error.message);
    } finally {
      submittingResultRef.current = false;
      setSubmittingResult(false);
    }
  };

  const completeTestRun = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/testruns/${executingRun.id}/complete`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        showModal('Run Completed', 'Test run completed successfully.');
        resetExecutionState();
        fetchAllTestRuns();
      }
    } catch (error) {
      console.error('Error completing test run:', error);
    }
  };

  const currentTest = getCurrentTest();
  const passCount = Object.values(testResults).filter(r => r.status === 'Pass').length;
  const failCount = Object.values(testResults).filter(r => r.status === 'Fail').length;
  const blockCount = Object.values(testResults).filter(r => r.status === 'Blocked').length;
  const visibleTestRuns = selectedProject
    ? testRuns.filter((run) => String(run.project_id) === String(selectedProject))
    : testRuns;

  return (
    <div className="test-execution-page">
      <div className="container">
        <h1>Test Execution</h1>
        <p className="page-description">
          {canManage
            ? 'Run test suites and track results in real time.'
            : 'Browse test runs and review their results.'}
        </p>

        {!executingRun && (
          <>
            <div className="filters">
              <div className="filter-group">
                <label htmlFor="project">Project:</label>
                <select
                  id="project"
                  value={selectedProject}
                  onChange={(e) => handleProjectChange(e.target.value)}
                >
                  <option value="">Select a project</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              {canManage && selectedProject && (
                <div className="filter-group">
                  <label htmlFor="suite">Test Suite:</label>
                  <select
                    id="suite"
                    value={selectedSuite}
                    onChange={(e) => handleSuiteChange(e.target.value)}
                  >
                    <option value="">Select a suite</option>
                    {suites.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {canManage && selectedSuite && testCases.length > 0 && (
              <>
                <div className="test-selection">
                  <h2>Select Test Cases to Execute ({selectedTestCases.length} selected)</h2>
                  <div className="test-cases-list">
                    {testCases.map(tc => (
                      <label key={tc.id} className="test-case-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedTestCases.includes(tc.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTestCases([...selectedTestCases, tc.id]);
                            } else {
                              setSelectedTestCases(selectedTestCases.filter(id => id !== tc.id));
                            }
                          }}
                        />
                        <span>{tc.name} <small>({tc.priority})</small></span>
                      </label>
                    ))}
                  </div>
                </div>

                {selectedTestCases.length > 0 && (
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowCreateRun(true)}
                  >
                    Start Test Run
                  </button>
                )}
              </>
            )}

            {canManage && showCreateRun && (
              <div className="card form-card">
                <h2>Create New Test Run</h2>
                <form onSubmit={handleCreateRun}>
                  <div className="form-group">
                    <label htmlFor="runName">Test Run Name</label>
                    <input
                      id="runName"
                      type="text"
                      value={runName}
                      onChange={(e) => setRunName(e.target.value)}
                      placeholder="e.g., Regression Test - Build 123"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="ticketUrl">Ticket link (optional)</label>
                    <input
                      id="ticketUrl"
                      type="url"
                      value={ticketUrl}
                      onChange={(e) => setTicketUrl(e.target.value)}
                      placeholder="https://…"
                    />
                    <p className="help-text">Link to the tracker ticket this run covers.</p>
                  </div>
                  <div className="form-group">
                    <label htmlFor="prUrl">PR link (optional)</label>
                    <input
                      id="prUrl"
                      type="url"
                      value={prUrl}
                      onChange={(e) => setPrUrl(e.target.value)}
                      placeholder="https://…"
                    />
                    <p className="help-text">Link to the pull request under test.</p>
                  </div>
                  {runFormError && <div className="error-banner">{runFormError}</div>}
                  <button type="submit" className="btn btn-primary" disabled={creatingRun}>
                    {creatingRun ? 'Starting...' : 'Start Execution'}
                  </button>
                </form>
              </div>
            )}

            <div className="test-runs-section">
              <h2>Previous Test Runs</h2>
              {selectedProject ? (
                <p className="help-text">Showing runs for the selected project only.</p>
              ) : (
                <p className="help-text">Showing runs from all projects. Select a project to filter.</p>
              )}

              {visibleTestRuns.length === 0 ? (
                <div className="empty-state">
                  <p>{selectedProject ? 'No test runs found for this project yet.' : 'No test runs available yet.'}</p>
                  {canManage && (
                    <p className="empty-state-action-subtext">
                      {selectedSuite && testCases.length > 0
                        ? 'Select test cases above and start your first run.'
                        : 'Choose a project and suite, then start a test run when you are ready.'}
                    </p>
                  )}
                </div>
              ) : (
                <div className="test-runs-list">
                  {visibleTestRuns.map(run => (
                    <div key={run.id} className="run-row">
                      <div className="run-row-main">
                        <span className={`run-status-dot run-status-dot-${run.status === 'Completed' ? 'completed' : 'progress'}`} />
                        <div>
                          <div className="run-row-name">{run.name}</div>
                          <div className="run-row-meta">
                            {run.project_name || `Project ${run.project_id}`} &middot; {new Date(run.created_at).toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <div className="run-row-actions">
                        <span className={`status-badge status-${run.status === 'Completed' ? 'pass' : 'progress'}`}>{run.status}</span>
                        <button
                          className="btn btn-small btn-secondary"
                          onClick={() => navigate(`/test-runs/${run.id}`)}
                        >
                          View Results
                        </button>
                        {canManage && run.status !== 'Completed' && (
                          <button
                            className="btn btn-small btn-primary"
                            onClick={() => handleResumeRun(run.id)}
                            disabled={resumingRunId === run.id}
                          >
                            {resumingRunId === run.id ? 'Loading...' : 'Continue Run'}
                          </button>
                        )}
                        <CopyLinkButton url={runShareUrl(run.id)} label="Copy link" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {executingRun && (
          <div className="execution-container">
            <div className="execution-header">
              <h2>{executingRun.name}</h2>
              <div className="progress-info">
                <span>Pass: {passCount}</span>
                <span>Fail: {failCount}</span>
                <span>Blocked: {blockCount}</span>
                <span>Progress: {currentTestIndex + 1}/{activeSelectedTestCases.length}</span>
              </div>
              <button className="btn btn-secondary" onClick={resetExecutionState}>
                Save And Exit
              </button>
            </div>

            {currentTest && (
              <div className="test-executor">
                <div className="test-details card">
                  <h3>{currentTest.name}</h3>
                  <p><strong>Priority:</strong> {currentTest.priority}</p>
                  <p><strong>Category:</strong> {currentTest.category || 'N/A'}</p>

                  {currentTest.preconditions && (
                    <div>
                      <strong>Preconditions:</strong>
                      <p>{currentTest.preconditions}</p>
                    </div>
                  )}

                  {currentTest.description && (
                    <div>
                      <strong>Description:</strong>
                      <p>{currentTest.description}</p>
                    </div>
                  )}

                  {currentTest.steps && currentTest.steps.length > 0 && (
                    <div>
                      <strong>Test Steps:</strong>
                      <ol>
                        {currentTest.steps.map((step, idx) => (
                          <li key={idx}>{step.description}</li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {currentTest.expected_result && (
                    <div>
                      <strong>Expected Result:</strong>
                      <p>{currentTest.expected_result}</p>
                    </div>
                  )}

                  <div className="form-group case-comment-field">
                    <label htmlFor="caseComment">Comment (optional)</label>
                    <textarea
                      id="caseComment"
                      value={caseComment}
                      onChange={(e) => setCaseComment(e.target.value)}
                      placeholder="Add any context, especially useful for a Fail or Skipped result…"
                      rows="2"
                      disabled={submittingResult}
                    />
                  </div>
                </div>

                <div className="result-buttons">
                  <button
                    className="btn btn-success"
                    onClick={() => handleTestResult('Pass')}
                    disabled={submittingResult}
                  >
                    ✓ Pass
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleTestResult('Fail')}
                    disabled={submittingResult}
                  >
                    ✗ Fail
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleTestResult('Blocked')}
                    disabled={submittingResult}
                  >
                    ⊘ Blocked
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleTestResult('Skipped')}
                    disabled={submittingResult}
                  >
                    ⊳ Skipped
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <AppModal
        open={modalState.open}
        title={modalState.title}
        message={modalState.message}
        onClose={closeModal}
      />
    </div>
  );
}

export default TestExecutionPage;
